import {
	CLAUSE_TYPE_COMPOUND,
	CLAUSE_TYPE_TERMINAL,
	COMPOUND_OPERATOR_AND,
	COMPOUND_OPERATOR_OR,
	type Jast,
	type TerminalClause,
} from '@atlaskit/jql-ast';
import { validateClause } from './validateClause.tsx';

type Result = {
	isValid: boolean;
	clause: TerminalClause | undefined;
};

/**
 * getRootLevelClause will return the clause and validity if it satisifies these conditions:
 * - there is no clause
 * - the clause is at the root level and connected by AND operator
 * We don't use a visitor and access query directly because we only need to check the root level
 */
export const getRootLevelClause = (ast: Jast, jqlTerm: string): Result => {
	let found = false;
	const whereClause = ast?.query?.where;

	// If there is no clause, we consider the query valid
	if (!whereClause) {
		return { isValid: true, clause: undefined };
	}

	// Only 1 terminal clause at root level
	if (whereClause.clauseType === CLAUSE_TYPE_TERMINAL) {
		// Check if jqlTerm is present and has valid operators (i.e. = or IN)
		if (whereClause.field.value.toLowerCase() === jqlTerm.toLowerCase()) {
			return { isValid: validateClause(whereClause), clause: whereClause };
		}

		// If it's not present, we consider the JQL valid
		return { isValid: true, clause: undefined };
	}

	if (whereClause.clauseType === CLAUSE_TYPE_COMPOUND) {
		const compoundClause = whereClause;

		// If root level has AND operator, just check there's maximum one jqlTerm at root level
		if (compoundClause.operator.value === COMPOUND_OPERATOR_AND) {
			let assigneeClause: TerminalClause | undefined;
			let isValid = true;
			for (const clause of compoundClause.clauses) {
				if (clause.clauseType === CLAUSE_TYPE_TERMINAL) {
					if (clause.field.value.toLowerCase() === jqlTerm.toLowerCase()) {
						if (found) {
							// More than one jqlTerm found at root level
							isValid = false;
							assigneeClause = undefined;
						} else {
							found = true;
							isValid = validateClause(clause);
							assigneeClause = clause;
						}
					}
				}
			}
			return { isValid, clause: assigneeClause };
		}

		// If root level has OR operator, check if jqlTerm is present
		// If jqlTerm is present, it's invalid (no support for OR operator at root level)
		// If jqlTerm is not present, it's valid (we don't care about other clauses)
		if (compoundClause.operator.value === COMPOUND_OPERATOR_OR) {
			const jqlTermClause = compoundClause.clauses.find(
				(clause): clause is TerminalClause =>
					clause.clauseType === CLAUSE_TYPE_TERMINAL &&
					clause.field.value.toLowerCase() === jqlTerm.toLowerCase(),
			);
			if (jqlTermClause) {
				return { isValid: false, clause: jqlTermClause };
			}
			return { isValid: true, clause: undefined };
		}

		return { isValid: false, clause: undefined };
	}
	return { isValid: false, clause: undefined };
};
